import { useState } from 'react';
import {
  Box, Typography, TextField, Button, Grid, FormControl,
  Select, MenuItem, FormControlLabel, Checkbox, Card, CardContent,
  LinearProgress, Chip, Stack, Radio, RadioGroup, Paper, IconButton,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import AnimatedSection from '../common/AnimatedSection';
import { jobs as staticJobs } from '../../data/jobs';
import { useJobs } from '../../hooks/usePublicApi';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import ConstructionIcon from '@mui/icons-material/Construction';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import MessageIcon from '@mui/icons-material/Message';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SendIcon from '@mui/icons-material/Send';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';

const projectTypeOptions = [
  { value: 'neubau', label: 'Neubau', subtitle: 'Innenausbau für Neubauten', icon: HomeWorkIcon },
  { value: 'umbau', label: 'Umbau', subtitle: 'Umbau & Modernisierung', icon: ConstructionIcon },
  { value: 'sanierung', label: 'Sanierung', subtitle: 'Sanierung & Renovierung', icon: AutoFixHighIcon },
];

export default function InquiryForm() {
  const [searchParams] = useSearchParams();
  const inquiryType = searchParams.get('type') || 'project';
  const position = searchParams.get('position') || '';
  const isJobApplication = inquiryType === 'job';
  const { jobs: apiJobs } = useJobs();
  const jobTitles = (apiJobs || staticJobs).map((j) => j.title);

  const [activeStep, setActiveStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitError, setSubmitError] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [projectFiles, setProjectFiles] = useState([]);
  const { control, handleSubmit, trigger, formState: { errors } } = useForm({
    defaultValues: {
      projectType: isJobApplication ? position : '',
      projectSize: '',
      timeline: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      message: isJobApplication ? `Bewerbung für: ${position}` : '',
      privacy: false,
      inquiry_type: inquiryType,
    },
  });

  const totalSteps = 3;
  const progress = ((activeStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    let fieldsToValidate = [];
    if (activeStep === 0) {
      fieldsToValidate = isJobApplication ? (position ? [] : ['projectType']) : ['projectType'];
      // For job applications, require CV on step 0
      if (isJobApplication && !cvFile) {
        alert('Bitte laden Sie Ihren Lebenslauf hoch');
        return;
      }
    }
    if (activeStep === 1) fieldsToValidate = ['firstName', 'lastName', 'email', 'phone'];
    if (activeStep === 2) fieldsToValidate = ['message', 'privacy'];

    const valid = await trigger(fieldsToValidate);
    if (valid) {
      if (activeStep === totalSteps - 1) {
        handleSubmit(onSubmit)();
      } else {
        setActiveStep((prev) => prev + 1);
      }
    }
  };

  const handleCvUpload = (event) => {
    const file = event.target.files[0];
    if (file) setCvFile(file);
  };

  const handleAdditionalFilesUpload = (event) => {
    const files = Array.from(event.target.files);
    setAdditionalFiles(prev => [...prev, ...files]);
  };

  const removeAdditionalFile = (index) => {
    setAdditionalFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleProjectFilesUpload = (event) => {
    const files = Array.from(event.target.files);
    setProjectFiles(prev => [...prev, ...files]);
  };

  const removeProjectFile = (index) => {
    setProjectFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const onSubmit = (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => formData.append(key, data[key]));
    if (isJobApplication) {
      if (cvFile) formData.append('cv', cvFile);
      additionalFiles.forEach((file, index) => formData.append(`additional_file_${index}`, file));
    } else {
      projectFiles.forEach((file, index) => formData.append(`project_file_${index}`, file));
    }

    setUploading(true);
    setUploadProgress(0);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/public/inquiries/submit');

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        setUploadProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onloadend = () => {
      setUploading(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        setSubmitted(true);
      } else {
        let msg = 'Die Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.';
        try {
          const r = JSON.parse(xhr.responseText);
          if (r.error) msg = r.error;
        } catch {}
        setSubmitError(msg);
      }
    };

    xhr.onerror = () => {
      setUploading(false);
      setSubmitError('Netzwerkfehler. Bitte überprüfen Sie Ihre Verbindung und versuchen Sie es erneut.');
    };

    xhr.send(formData);
  };

  if (uploading) {
    return (
      <AnimatedSection>
        <Card
          sx={{
            maxWidth: 600,
            mx: 'auto',
            textAlign: 'center',
            background: 'linear-gradient(135deg, #FFFFFF 0%, #FAFAFA 100%)',
            border: '1px solid rgba(209, 188, 138, 0.2)',
          }}
        >
          <CardContent sx={{ p: 6 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(209, 188, 138, 0.15) 0%, rgba(209, 188, 138, 0.05) 100%)',
                border: '2px solid rgba(209, 188, 138, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <UploadFileIcon sx={{ color: '#D1BC8A', fontSize: 40 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {isJobApplication ? 'Bewerbung wird gesendet…' : 'Anfrage wird gesendet…'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Bitte warten Sie, während wir Ihre Dateien hochladen.
            </Typography>
            <Box sx={{ mb: 1.5 }}>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: 'rgba(209, 188, 138, 0.15)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 5,
                    background: 'linear-gradient(90deg, #D1BC8A 0%, #B8A06E 100%)',
                  },
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
              {uploadProgress}%
            </Typography>
          </CardContent>
        </Card>
      </AnimatedSection>
    );
  }

  if (submitError) {
    return (
      <AnimatedSection>
        <Card
          sx={{
            maxWidth: 600,
            mx: 'auto',
            textAlign: 'center',
            background: 'linear-gradient(135deg, #FFFFFF 0%, #FAFAFA 100%)',
            border: '2px solid',
            borderColor: 'error.main',
          }}
        >
          <CardContent sx={{ p: 6 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'rgba(211,47,47,0.08)',
                border: '2px solid rgba(211,47,47,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 44 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: 'error.main' }}>
              Fehler beim Senden
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              {submitError}
            </Typography>
            <Button
              variant="contained"
              onClick={() => setSubmitError(null)}
              sx={{
                background: 'linear-gradient(135deg, #D1BC8A 0%, #B8A06E 100%)',
                color: '#0F1D2E',
                fontWeight: 700,
                px: 4,
              }}
            >
              Erneut versuchen
            </Button>
          </CardContent>
        </Card>
      </AnimatedSection>
    );
  }

  if (submitted) {
    return (
      <AnimatedSection>
        <Card
          sx={{
            maxWidth: 600,
            mx: 'auto',
            textAlign: 'center',
            background: 'linear-gradient(135deg, #FFFFFF 0%, #FAFAFA 100%)',
            border: '2px solid',
            borderColor: 'primary.main',
          }}
        >
          <CardContent sx={{ p: 6 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #D1BC8A 0%, #B8A06E 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                boxShadow: '0 8px 24px rgba(209, 188, 138, 0.4)',
              }}
            >
              <CheckCircleIcon sx={{ color: '#0F1D2E', fontSize: 48 }} />
            </Box>
            <Typography variant="h3" sx={{ mb: 2, fontWeight: 700 }}>
              {isJobApplication ? 'Bewerbung gesendet!' : 'Vielen Dank!'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {isJobApplication
                ? 'Ihre Bewerbung wurde erfolgreich gesendet. Wir melden uns in Kürze bei Ihnen.'
                : 'Ihre Anfrage wurde erfolgreich gesendet. Wir melden uns innerhalb von 24 Stunden bei Ihnen.'}
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 3,
                py: 1.5,
                borderRadius: 3,
                background: 'linear-gradient(135deg, rgba(209, 188, 138, 0.1) 0%, rgba(209, 188, 138, 0.05) 100%)',
                border: '1px solid rgba(209, 188, 138, 0.3)',
              }}
            >
              <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                Wir antworten innerhalb von 24 Stunden
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </AnimatedSection>
    );
  }

  return (
    <Box>
      {/* Progress Bar */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Schritt {activeStep + 1} von {totalSteps}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {Math.round(progress)}% abgeschlossen
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: 'rgba(209, 188, 138, 0.1)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              background: 'linear-gradient(90deg, #D1BC8A 0%, #B8A06E 100%)',
            },
          }}
        />
      </Box>

      <AnimatedSection key={activeStep}>
        <Card
          sx={{
            background: 'linear-gradient(135deg, #FFFFFF 0%, #FAFAFA 100%)',
            border: '1px solid rgba(209, 188, 138, 0.2)',
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          {/* Step Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, rgba(209, 188, 138, 0.1) 0%, rgba(209, 188, 138, 0.05) 100%)',
              borderBottom: '1px solid rgba(209, 188, 138, 0.2)',
              p: 3,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
              {activeStep === 0 && (isJobApplication ? 'Bewerbungsdetails' : 'Projektdetails')}
              {activeStep === 1 && 'Ihre Kontaktdaten'}
              {activeStep === 2 && 'Projektbeschreibung'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {activeStep === 0 && 'Wählen Sie die Art Ihres Projekts'}
              {activeStep === 1 && 'Wie können wir Sie erreichen?'}
              {activeStep === 2 && 'Erzählen Sie uns mehr über Ihr Vorhaben'}
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            {/* Step 0: Project Details */}
            {activeStep === 0 && !isJobApplication && (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
                    Wählen Sie Ihren Projekttyp *
                  </Typography>
                  <Controller
                    name="projectType"
                    control={control}
                    rules={{ required: 'Bitte wählen Sie einen Projekttyp' }}
                    render={({ field }) => (
                      <RadioGroup {...field}>
                        <Grid container spacing={2}>
                          {projectTypeOptions.map((option) => {
                            const OptionIcon = option.icon;
                            const isSelected = field.value === option.value;
                            return (
                              <Grid size={{ xs: 12, sm: 4 }} key={option.value}>
                                <Paper
                                  sx={{
                                    p: 3,
                                    cursor: 'pointer',
                                    border: '2px solid',
                                    borderColor: isSelected ? 'primary.main' : 'rgba(209, 188, 138, 0.2)',
                                    background: isSelected
                                      ? 'linear-gradient(135deg, rgba(209, 188, 138, 0.15) 0%, rgba(209, 188, 138, 0.05) 100%)'
                                      : 'white',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                      borderColor: 'primary.main',
                                      transform: 'translateY(-4px)',
                                      boxShadow: '0 8px 24px rgba(209, 188, 138, 0.2)',
                                    },
                                  }}
                                  onClick={() => field.onChange(option.value)}
                                >
                                  <FormControlLabel
                                    value={option.value}
                                    control={<Radio sx={{ display: 'none' }} />}
                                    label={
                                      <Box>
                                        <Box
                                          sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 3,
                                            background: isSelected
                                              ? 'linear-gradient(135deg, #D1BC8A 0%, #B8A06E 100%)'
                                              : 'linear-gradient(135deg, rgba(209, 188, 138, 0.15) 0%, rgba(209, 188, 138, 0.08) 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 2,
                                          }}
                                        >
                                          <OptionIcon sx={{ color: isSelected ? '#0F1D2E' : '#D1BC8A', fontSize: 32 }} />
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                          {option.label}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                          {option.subtitle}
                                        </Typography>
                                      </Box>
                                    }
                                    sx={{ m: 0, width: '100%' }}
                                  />
                                </Paper>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </RadioGroup>
                    )}
                  />
                  {errors.projectType && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      {errors.projectType.message}
                    </Typography>
                  )}
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <SquareFootIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Projektgröße
                    </Typography>
                  </Box>
                  <Controller
                    name="projectSize"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <Select {...field} displayEmpty>
                          <MenuItem value="" disabled>
                            Bitte wählen
                          </MenuItem>
                          <MenuItem value="unter-50">Unter 50 m²</MenuItem>
                          <MenuItem value="50-100">50 — 100 m²</MenuItem>
                          <MenuItem value="100-200">100 — 200 m²</MenuItem>
                          <MenuItem value="200-500">200 — 500 m²</MenuItem>
                          <MenuItem value="ueber-500">Über 500 m²</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <CalendarTodayIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Zeitrahmen
                    </Typography>
                  </Box>
                  <Controller
                    name="timeline"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <Select {...field} displayEmpty>
                          <MenuItem value="" disabled>
                            Bitte wählen
                          </MenuItem>
                          <MenuItem value="sofort">Sofort / So schnell wie möglich</MenuItem>
                          <MenuItem value="1-3">In 1—3 Monaten</MenuItem>
                          <MenuItem value="3-6">In 3—6 Monaten</MenuItem>
                          <MenuItem value="6+">In mehr als 6 Monaten</MenuItem>
                          <MenuItem value="flexibel">Flexibel</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                {/* File Upload */}
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <AttachFileIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Dateien anhängen (optional)
                    </Typography>
                  </Box>
                  <Paper
                    sx={{
                      p: 3,
                      border: '2px dashed',
                      borderColor: projectFiles.length > 0 ? 'primary.main' : 'rgba(209, 188, 138, 0.3)',
                      background: projectFiles.length > 0
                        ? 'linear-gradient(135deg, rgba(209, 188, 138, 0.08) 0%, rgba(209, 188, 138, 0.03) 100%)'
                        : 'white',
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        background: 'rgba(209, 188, 138, 0.05)',
                      },
                    }}
                    onClick={() => document.getElementById('project-files-upload').click()}
                  >
                    <input
                      id="project-files-upload"
                      type="file"
                      multiple
                      onChange={handleProjectFilesUpload}
                      style={{ display: 'none' }}
                    />
                    <UploadFileIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Klicken Sie hier, um Dateien hochzuladen
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Alle Dateitypen erlaubt — Pläne, Fotos, Dokumente etc.
                    </Typography>
                  </Paper>

                  {projectFiles.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      {projectFiles.map((file, index) => (
                        <Paper
                          key={index}
                          sx={{
                            p: 1.5, mb: 1,
                            display: 'flex', alignItems: 'center', gap: 1.5,
                            background: 'linear-gradient(135deg, rgba(209, 188, 138, 0.05) 0%, transparent 100%)',
                            border: '1px solid rgba(209, 188, 138, 0.2)',
                          }}
                        >
                          <DescriptionIcon sx={{ color: 'primary.main', fontSize: 24, flexShrink: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {file.size < 1024 * 1024
                                ? `${(file.size / 1024).toFixed(0)} KB`
                                : `${(file.size / 1024 / 1024).toFixed(2)} MB`}
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeProjectFile(index); }} sx={{ color: 'error.main' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Grid>
              </Grid>
            )}

            {activeStep === 0 && isJobApplication && (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  {position ? (
                    <Paper
                      sx={{
                        p: 3,
                        background: 'linear-gradient(135deg, rgba(209, 188, 138, 0.1) 0%, rgba(209, 188, 138, 0.05) 100%)',
                        border: '2px solid rgba(209, 188, 138, 0.3)',
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        Position
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {position}
                      </Typography>
                    </Paper>
                  ) : (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <WorkOutlineIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Gewünschte Position *
                        </Typography>
                      </Box>
                      <Controller
                        name="projectType"
                        control={control}
                        rules={{ required: 'Bitte wählen Sie eine Position' }}
                        render={({ field }) => (
                          <FormControl fullWidth>
                            <Select {...field} displayEmpty>
                              <MenuItem value="" disabled>Bitte wählen</MenuItem>
                              {jobTitles.map((title) => (
                                <MenuItem key={title} value={title}>{title}</MenuItem>
                              ))}
                              <MenuItem value="Initiativbewerbung">Initiativbewerbung</MenuItem>
                            </Select>
                            {errors.projectType && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {errors.projectType.message}
                              </Typography>
                            )}
                          </FormControl>
                        )}
                      />
                    </Box>
                  )}
                </Grid>

                {/* CV Upload */}
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <UploadFileIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Lebenslauf (CV) *
                    </Typography>
                  </Box>
                  <Paper
                    sx={{
                      p: 3,
                      border: '2px dashed',
                      borderColor: cvFile ? 'primary.main' : 'rgba(209, 188, 138, 0.3)',
                      background: cvFile ? 'linear-gradient(135deg, rgba(209, 188, 138, 0.08) 0%, rgba(209, 188, 138, 0.03) 100%)' : 'white',
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        background: 'linear-gradient(135deg, rgba(209, 188, 138, 0.1) 0%, rgba(209, 188, 138, 0.05) 100%)',
                      },
                    }}
                    onClick={() => document.getElementById('cv-upload').click()}
                  >
                    <input
                      id="cv-upload"
                      type="file"
                      onChange={handleCvUpload}
                      style={{ display: 'none' }}
                    />
                    {cvFile ? (
                      <Box>
                        <DescriptionIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {cvFile.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {(cvFile.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCvFile(null);
                          }}
                          sx={{ mt: 1, display: 'block', mx: 'auto' }}
                        >
                          Entfernen
                        </Button>
                      </Box>
                    ) : (
                      <Box>
                        <UploadFileIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Klicken Sie hier, um Ihren Lebenslauf hochzuladen
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Alle Dateitypen erlaubt
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>

                {/* Additional Files Upload */}
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <AttachFileIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Weitere Dokumente (optional)
                    </Typography>
                  </Box>
                  <Paper
                    sx={{
                      p: 2,
                      border: '2px dashed rgba(209, 188, 138, 0.3)',
                      background: 'white',
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        background: 'rgba(209, 188, 138, 0.05)',
                      },
                    }}
                    onClick={() => document.getElementById('additional-upload').click()}
                  >
                    <input
                      id="additional-upload"
                      type="file"
                      multiple
                      onChange={handleAdditionalFilesUpload}
                      style={{ display: 'none' }}
                    />
                    <AttachFileIcon sx={{ fontSize: 32, color: 'primary.main', mb: 0.5 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Anschreiben, Zeugnisse, Zertifikate etc.
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                      Mehrere Dateien möglich — alle Typen erlaubt
                    </Typography>
                  </Paper>

                  {/* Display uploaded additional files */}
                  {additionalFiles.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      {additionalFiles.map((file, index) => (
                        <Paper
                          key={index}
                          sx={{
                            p: 1.5,
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            background: 'linear-gradient(135deg, rgba(209, 188, 138, 0.05) 0%, transparent 100%)',
                            border: '1px solid rgba(209, 188, 138, 0.2)',
                          }}
                        >
                          <DescriptionIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => removeAdditionalFile(index)}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Grid>
              </Grid>
            )}

            {/* Step 1: Contact Details */}
            {activeStep === 1 && (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Vorname *
                    </Typography>
                  </Box>
                  <Controller
                    name="firstName"
                    control={control}
                    rules={{ required: 'Bitte geben Sie Ihren Vornamen ein' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Max"
                        error={!!errors.firstName}
                        helperText={errors.firstName?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Nachname *
                    </Typography>
                  </Box>
                  <Controller
                    name="lastName"
                    control={control}
                    rules={{ required: 'Bitte geben Sie Ihren Nachnamen ein' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Mustermann"
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <EmailIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      E-Mail *
                    </Typography>
                  </Box>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: 'Bitte geben Sie Ihre E-Mail-Adresse ein',
                      pattern: { value: /^\S+@\S+\.\S+$/, message: 'Ungültige E-Mail-Adresse' },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="email"
                        placeholder="max@beispiel.de"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <PhoneIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Telefon *
                    </Typography>
                  </Box>
                  <Controller
                    name="phone"
                    control={control}
                    rules={{ required: 'Bitte geben Sie Ihre Telefonnummer ein' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="+49 89 123 456 78"
                        error={!!errors.phone}
                        helperText={errors.phone?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <BusinessIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Firma (optional)
                    </Typography>
                  </Box>
                  <Controller
                    name="company"
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} fullWidth placeholder="Ihre Firma GmbH" />
                    )}
                  />
                </Grid>
              </Grid>
            )}

            {/* Step 2: Message */}
            {activeStep === 2 && (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <MessageIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Projektbeschreibung *
                    </Typography>
                  </Box>
                  <Controller
                    name="message"
                    control={control}
                    rules={{ required: 'Bitte beschreiben Sie Ihr Projekt' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        rows={6}
                        error={!!errors.message}
                        helperText={errors.message?.message}
                        placeholder="Beschreiben Sie Ihr Projekt im Detail: Umfang, besondere Wünsche, Materialien, Budget usw."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'white',
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Paper
                    sx={{
                      p: 2.5,
                      background: 'linear-gradient(135deg, rgba(209, 188, 138, 0.08) 0%, rgba(209, 188, 138, 0.03) 100%)',
                      border: '1px solid rgba(209, 188, 138, 0.2)',
                    }}
                  >
                    <Controller
                      name="privacy"
                      control={control}
                      rules={{ required: 'Bitte stimmen Sie der Datenschutzerklärung zu' }}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              sx={{
                                '&.Mui-checked': { color: 'primary.main' },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                              Ich stimme der Verarbeitung meiner Daten gemäß der{' '}
                              <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                Datenschutzerklärung
                              </Box>{' '}
                              zu. *
                            </Typography>
                          }
                        />
                      )}
                    />
                    {errors.privacy && (
                      <Typography variant="caption" color="error" sx={{ ml: 4, display: 'block' }}>
                        {errors.privacy.message}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      </AnimatedSection>

      {/* Navigation Buttons */}
      <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
        <Button
          onClick={handleBack}
          disabled={activeStep === 0}
          variant="outlined"
          color="secondary"
          size="large"
          sx={{ flex: 1 }}
        >
          Zurück
        </Button>
        <Button
          onClick={handleNext}
          variant="contained"
          color="primary"
          size="large"
          endIcon={activeStep === totalSteps - 1 ? <SendIcon /> : null}
          sx={{
            flex: 2,
            background: 'linear-gradient(135deg, #D1BC8A 0%, #B8A06E 100%)',
          }}
        >
          {activeStep === totalSteps - 1 ? 'Anfrage senden' : 'Weiter'}
        </Button>
      </Stack>

      {/* Trust Indicators */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
          <Chip
            icon={<CheckCircleIcon />}
            label="Kostenlose Beratung"
            sx={{
              bgcolor: 'rgba(209, 188, 138, 0.1)',
              color: 'primary.main',
              fontWeight: 600,
              borderColor: 'primary.main',
            }}
            variant="outlined"
          />
          <Chip
            icon={<CheckCircleIcon />}
            label="24h Rückmeldung"
            sx={{
              bgcolor: 'rgba(209, 188, 138, 0.1)',
              color: 'primary.main',
              fontWeight: 600,
              borderColor: 'primary.main',
            }}
            variant="outlined"
          />
          <Chip
            icon={<CheckCircleIcon />}
            label="Unverbindliches Angebot"
            sx={{
              bgcolor: 'rgba(209, 188, 138, 0.1)',
              color: 'primary.main',
              fontWeight: 600,
              borderColor: 'primary.main',
            }}
            variant="outlined"
          />
        </Stack>
      </Box>
    </Box>
  );
}
